"use client"

import { useEffect, useRef, useCallback, useState } from "react"
import Image from "next/image"
import { AlertCircle, AlertTriangle, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useResize } from "@/hooks/use-resize"
import { DividerHandle } from "./divider-handle"
import { pathParent, useEditorStore } from "@/lib/editor-store"
import { normalizePath } from "@/lib/compiler/path-utils"
import { useAuth } from "@/lib/auth-context"
import { useThemeMode } from "@/lib/themes/theme-mode"
import { loadSvgByExtension } from "@/lib/file-icons"
import type { AwarenessUserState } from "@/lib/crdt/awareness-user"
import { ProjectSettingsNav } from "@/components/editor/project-settings-panel"
import { cn } from "@/lib/utils"
import {
  initUploadSession,
  savePendingUploadSessionBatch,
  uploadFileWithSession,
} from "@/lib/upload-session"
import {
  notifyUploadJobsChanged,
  patchLiveUploadJob,
  removeLiveUploadJob,
  upsertLiveUploadJob,
} from "@/lib/upload-job-store"
import { api } from "@/lib/api"
import { toast } from "sonner"
import {
  droppedFilesKey,
  filesFromDataTransfer,
  importFinderFiles,
  isExternalFileDrag,
} from "@/components/editor/explorer/tree-utils"

export type SidebarTab = "file" | "setting"

type ExplorerEvent =
  | { type: "FILE_OPEN"; path: string; name: string }
  | { type: "RENAME"; oldPath: string; newName: string }
  | { type: "DELETE"; path: string; name: string }
  | { type: "NEW_FILE"; parentPath: string; name: string }
  | { type: "NEW_FOLDER"; parentPath: string; name: string }
  | { type: "MOVE"; oldPath: string; newPath: string }
  | { type: "COPY"; name: string }
  | { type: "COPY_PATH"; path: string }
  | { type: "EXTERNAL_CLIPBOARD_PASTE"; targetPath: string }

/* Same localStorage record the main dashboard (dashboard-main-content.tsx) and Shared Access
   (dashboard-shared-access-content.tsx) write on Copy — read here so a file copied on either of
   those pages can be pasted into the editor's file explorer too. Only `ids`/`mode` are needed here;
   the rest of each page's own clipboard shape is irrelevant to this cross-app paste. */
const CROSS_APP_CLIPBOARD_KEY = "rudrise-clipboard"

function readCrossAppClipboard(): { ids: string[]; mode: "cut" | "copy" } | null {
  try {
    const raw = localStorage.getItem(CROSS_APP_CLIPBOARD_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || !Array.isArray(parsed.ids) || parsed.ids.length === 0) return null
    if (parsed.mode !== "cut" && parsed.mode !== "copy") return null
    return { ids: parsed.ids, mode: parsed.mode }
  } catch {
    return null
  }
}

type PendingDelete = {
  requestId: string
  node: { name: string; path: string; type: string }
}

const sidebarIcons = [
  { id: "file", src: "/side_menu_file.svg", title: "Explorer", size: 32 },
  { id: "setting", src: "/side_menu_setting.svg", title: "Settings", size: 32 },
]

const bottomSidebarIcons = [
  { id: "profile", src: "/side_menu_profile.svg", title: "Profile" },
  { id: "help", src: "/side_menu_help.svg", title: "Help" },
  { id: "logout", src: "/side_menu_logout.svg", title: "Logout" },
]

// Helper to detect common project folder prefix (e.g. templateId) to strip in iframe
const getProjectPrefix = (files: Record<string, any>) => {
  const paths = Object.keys(files)
  if (paths.length === 0) return ""

  const firstPath = paths[0]
  const parts = firstPath.split("/")
  if (parts.length <= 1) return "" // No folder prefix

  const firstSegment = parts[0]
  const allStartWithSegment = paths.every(
    (p) => p.startsWith(firstSegment + "/")
  )

  return allStartWithSegment ? firstSegment : ""
}

function filesFromDropMessage(value: unknown): File[] {
  if (!value) return []
  const list = Array.isArray(value)
    ? value
    : typeof value === "object" && value !== null && "length" in value
      ? Array.from(value as ArrayLike<unknown>)
      : []
  return list.filter((item): item is File => item instanceof File)
}

// Convert iframe path back to store path by adding prefix
const toStorePath = (iframePath: string, prefix: string) => {
  let rel = iframePath.replace(/^\/?project\/?/, "")
  if (rel.startsWith("/")) {
    rel = rel.slice(1)
  }
  if (prefix) {
    return rel ? `${prefix}/${rel}` : prefix
  }
  return rel
}

interface FileNode {
  name: string
  path: string
  type: "folder" | "file"
  isExpanded: boolean
  children: FileNode[]
}

// Build FileNode tree structure for Flutter iframe
const buildTree = (
  files: Record<string, any>,
  virtualFolders: string[],
  prefix: string,
  rootName: string,
): FileNode => {
  const root: FileNode = {
    name: rootName || "Project",
    path: "/project",
    type: "folder",
    isExpanded: true,
    children: [],
  }

  // forceFolder=true → last segment is a folder (used for virtual/empty folders)
  const addPath = (parts: string[], forceFolder = false) => {
    let current = root
    let currentPath = "/project"

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]
      currentPath = `${currentPath}/${part}`
      const isLast = i === parts.length - 1

      let child = current.children.find((c) => c.name === part)
      if (!child) {
        child = {
          name: part,
          path: currentPath,
          type: isLast && !forceFolder ? "file" : "folder",
          // New empty folders (last segment of a virtual-folder path) start closed.
          // Intermediate path segments and folders that contain files stay open.
          isExpanded: !(forceFolder && isLast),
          children: [],
        }
        current.children.push(child)
      } else if (isLast && forceFolder && child.type === "file") {
        // Upgrade a node from file to folder if virtualFolders says it's a folder
        child.type = "folder"
      }
      current = child
    }
  }

  // Add file paths (last segment = file)
  Object.keys(files).forEach((path) => {
    let relativePath = path
    if (prefix && path.startsWith(prefix + "/")) {
      relativePath = path.slice(prefix.length + 1)
    }
    const parts = relativePath.split("/").filter(Boolean)
    if (parts.length > 0) addPath(parts, false)
  })

  // Add virtual/empty folders (all segments including last = folder)
  virtualFolders.forEach((folder) => {
    let relativePath = folder
    if (prefix && folder.startsWith(prefix + "/")) {
      relativePath = folder.slice(prefix.length + 1)
    }
    const parts = relativePath.split("/").filter(Boolean)
    if (parts.length > 0) addPath(parts, true)
  })

  // Sort nodes: folders first, then files alphabetically
  const sortNodes = (nodes: FileNode[]): FileNode[] => {
    return nodes
      .map((node) => ({ ...node, children: sortNodes(node.children) }))
      .sort((a, b) => {
        if (a.type !== b.type) return a.type === "folder" ? -1 : 1
        return a.name.localeCompare(b.name)
      })
  }

  root.children = sortNodes(root.children)
  return root
}

export function FileExplorerFrame({
  activeTab: controlledTab,
  onActiveTabChange,
  collapsed: controlledCollapsed,
  onCollapsedChange,
}: {
  activeTab?: SidebarTab
  onActiveTabChange?: (tab: SidebarTab) => void
  collapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void
} = {}) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [internalTab, setInternalTab] = useState<SidebarTab>("file")
  const activeTab = controlledTab ?? internalTab
  const setActiveTab = (tab: SidebarTab) => {
    if (controlledTab === undefined) setInternalTab(tab)
    onActiveTabChange?.(tab)
  }
  const [frameReady, setFrameReady] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null)
  const [internalCollapsed, setInternalCollapsed] = useState(false)
  const panelCollapsed = controlledCollapsed ?? internalCollapsed
  const setPanelCollapsed = (next: boolean) => {
    if (controlledCollapsed === undefined) setInternalCollapsed(next)
    onCollapsedChange?.(next)
  }

  const {
    state, projectName, projectId, openFile, renamePath, deletePath, createFileAtPath, createFolder,
    getFolderId, refreshFileTree, getYjsBinding, moveClipboardFiles, updateContent,
  } = useEditorStore()
  const { isAuthenticated, logout } = useAuth()
  const { theme } = useThemeMode()
  // One XHR in flight at a time for the drop-to-upload queue below — same object uploadFileWithSession
  // uses internally to abort the in-flight part on pause/cancel (not exposed here, uploads just run
  // to completion or fail; there's no pause/cancel UI for this batch).
  const uploadXhrRef = useRef<XMLHttpRequest | null>(null)
  const explorerPanelRef = useRef<HTMLDivElement>(null)
  const recentOsDropsRef = useRef<Map<string, number>>(new Map())

  // Auth users receive clean nested paths from buildProjectTree — no prefix stripping needed.
  const prefix = isAuthenticated ? "" : getProjectPrefix(state.files)

  const postFilesToIframe = useCallback(() => {
    const win = iframeRef.current?.contentWindow
    if (!win) return

    // Auth sessions are per-project now, so main.tex/main.pdf are real project files
    // (often the designated main file), not leftover template-session artefacts.
    const tree = buildTree(state.files, state.virtualFolders, prefix, projectName || "Project")
    
    try {
      // Send multiple formats/events to ensure the Dart app can decode it however it expects:
      win.postMessage(JSON.stringify({ type: "SET_PROJECT", project: tree }), "*")
      win.postMessage(JSON.stringify({ type: "SET_FILES", files: tree }), "*")
      win.postMessage(JSON.stringify(tree), "*")
      win.postMessage({ type: "SET_PROJECT", project: tree }, "*")
      win.postMessage({ type: "SET_FILES", files: tree }, "*")
      win.postMessage(tree, "*")
    } catch (e) {
      console.warn("Failed to post message to iframe:", e)
    }
  }, [state.files, state.virtualFolders, prefix, projectName, isAuthenticated])

  // Sync files whenever frame is ready or files update
  useEffect(() => {
    if (frameReady) {
      postFilesToIframe()
    }
  }, [frameReady, postFilesToIframe])

  // Icons are static, so fetch/send them once per frame load rather than on every file change.
  useEffect(() => {
    if (!frameReady) return
    const win = iframeRef.current?.contentWindow
    if (!win) return

    let cancelled = false
    loadSvgByExtension()
      .then((svgByExtension) => {
        if (cancelled) return
        win.postMessage(JSON.stringify({ type: "SET_ICONS", svgByExtension }), "*")
      })
      .catch((e) => console.warn("Failed to load explorer icons:", e))

    return () => {
      cancelled = true
    }
  }, [frameReady])

  // Push the app's shared theme (mode + palette) to the Flutter explorer so it can
  // restyle itself — same hex values already used across the header/ribbon/tabs/console.
  const postThemeToIframe = useCallback(() => {
    const win = iframeRef.current?.contentWindow
    if (!win) return
    const payload = {
      type: "SET_THEME",
      mode: theme.name === "light" ? "light" : "dark",
      colors: { ...theme.editorPanels.explorerFrame },
    }
    try {
      win.postMessage(JSON.stringify(payload), "*")
    } catch (e) {
      console.warn("Failed to post theme to iframe:", e)
    }
  }, [theme])

  useEffect(() => {
    if (!frameReady) return
    postThemeToIframe()
  }, [frameReady, postThemeToIframe])

  // Tell the iframe whether a file copied or cut on the dashboard or Shared Access
  // (rudrise-clipboard in localStorage) is available to paste here.
  const postExternalClipboardStatus = useCallback(() => {
    const win = iframeRef.current?.contentWindow
    if (!win) return
    const clipboard = readCrossAppClipboard()
    const count = clipboard?.ids.length ?? 0
    win.postMessage(JSON.stringify({ type: "SET_EXTERNAL_CLIPBOARD", count }), "*")
  }, [])

  useEffect(() => {
    if (!frameReady) return
    postExternalClipboardStatus()
    // Same-tab writes (this page's own dashboard/shared-access, if ever mounted alongside) don't
    // fire "storage" — only other tabs do — but a fresh mount of this component (e.g. navigating
    // from the dashboard to the editor) already re-checks via the frameReady effect above.
    window.addEventListener("storage", postExternalClipboardStatus)
    return () => window.removeEventListener("storage", postExternalClipboardStatus)
  }, [frameReady, postExternalClipboardStatus])

  const postPresenceToIframe = useCallback(() => {
    const win = iframeRef.current?.contentWindow
    if (!win) return
    const binding = getYjsBinding()
    if (!binding) return

    const users: Array<{
      id: string
      user: AwarenessUserState
      activeFile: string | null
      isTyping: boolean
    }> = []

    binding.awareness.getStates().forEach((rawState, clientId) => {
      const awState = rawState as { user?: AwarenessUserState; activeFile?: string | null; isTyping?: boolean }
      if (!awState.user) return
      users.push({
        id: String(clientId),
        user: {
          name: awState.user.name,
          color: awState.user.color,
          colorLight: awState.user.colorLight,
          initials: awState.user.initials,
          username: awState.user.username,
          avatarUrl: awState.user.avatarUrl,
        },
        activeFile: awState.activeFile ?? null,
        isTyping: !!awState.isTyping,
      })
    })

    try {
      win.postMessage(JSON.stringify({ type: "SET_PRESENCE", users }), "*")
    } catch (e) {
      console.warn("Failed to post presence to iframe:", e)
    }
  }, [getYjsBinding])

  // Presence is live CRDT awareness state — resend on every peer change, and
  // re-subscribe whenever the Yjs provider is (re)created (crdtProviderEpoch bumps then).
  useEffect(() => {
    if (!frameReady) return
    const binding = getYjsBinding()
    if (!binding) return

    postPresenceToIframe()
    binding.awareness.on("change", postPresenceToIframe)
    binding.awareness.on("update", postPresenceToIframe)

    return () => {
      binding.awareness.off("change", postPresenceToIframe)
      binding.awareness.off("update", postPresenceToIframe)
    }
  }, [frameReady, state.crdtProviderEpoch, getYjsBinding, postPresenceToIframe])

  const bottomPanel = useResize({
    axis: "vertical",
    initial: 200,
    min: 120,
    max: 600,
    invert: true,
    storageKey: "layout-explorer-bottom",
    collapseSize: 120,
  })

  // Pasting the dashboard/Shared-Access clipboard here is a backend copy or move of an existing
  // resource, not a byte upload. Copy uses POST /files/copy (keep_both). Cut uses the same
  // versioned PUT /files/:id/move as a drag move, including the CRDT rename.
  const handleExternalClipboardPaste = useCallback(async (targetPath: string) => {
    const clipboard = readCrossAppClipboard()
    if (!clipboard) return

    const parentStorePath = toStorePath(targetPath, prefix)
    const parentId = parentStorePath ? getFolderId(parentStorePath) : undefined
    const count = clipboard.ids.length

    try {
      if (clipboard.mode === "copy") {
        // Root paste must send null. A folder paste must send that folder's file_id.
        // A missing folder id is not treated as a root copy.
        const targetParentId = parentStorePath ? (parentId ?? null) : null
        if (parentStorePath && !targetParentId) {
          toast.error("Could not copy into that folder")
          return
        }
        const res = await api.copyResources({
          conflict_policy: "keep_both",
          resource_ids: clipboard.ids,
          target_parent_id: targetParentId,
        })
        if (!res.success) return
      } else {
        const moved = await moveClipboardFiles(clipboard.ids, parentStorePath || null)
        if (!moved) {
          toast.error("Some items could not be moved")
          return
        }
      }

      toast.success(`${clipboard.mode === "cut" ? "Moved" : "Copied"} ${count} item${count !== 1 ? "s" : ""}`)
      localStorage.removeItem(CROSS_APP_CLIPBOARD_KEY)
      postExternalClipboardStatus()
      // Cut already refreshes from the versioned move. Refreshing here would race that
      // request and could register the moved file as a new CRDT create.
      if (clipboard.mode === "copy") refreshFileTree()
    } catch (err) {
      console.warn("[FileExplorerFrame] external clipboard paste failed:", err)
    }
  }, [prefix, getFolderId, postExternalClipboardStatus, refreshFileTree, moveClipboardFiles])

  const handleEvent = useCallback(
    (event: ExplorerEvent) => {
      switch (event.type) {
        case "FILE_OPEN": {
          const storePath = toStorePath(event.path, prefix)
          console.log("[FileExplorer] FILE_OPEN event:", { rawPath: event.path, prefix, storePath, name: event.name })
          openFile(storePath, event.name)
          break
        }

        case "RENAME": {
          const oldStorePath = toStorePath(event.oldPath, prefix)
          const parentStorePath = pathParent(oldStorePath)
          const newStorePath = normalizePath(parentStorePath ? `${parentStorePath}/${event.newName}` : event.newName)
          renamePath(oldStorePath, newStorePath)
          break
        }
        case "DELETE": {
          const storePath = toStorePath(event.path, prefix)
          console.log("[FileExplorerFrame] Received DELETE event for path:", event.path, "resolved store path:", storePath, "prefix:", prefix)
          deletePath(storePath)
          break
        }
        case "NEW_FILE": {
          const parentStorePath = toStorePath(event.parentPath, prefix)
          const path = normalizePath(
            parentStorePath ? `${parentStorePath}/${event.name}` : event.name,
          )
          createFileAtPath(path, "", false)
          break
        }
        case "NEW_FOLDER": {
          const parentStorePath = toStorePath(event.parentPath, prefix)
          const path = normalizePath(
            parentStorePath ? `${parentStorePath}/${event.name}` : event.name,
          )
          createFolder(path)
          break
        }
        case "MOVE": {
          const oldStorePath = toStorePath(event.oldPath, prefix)
          const newStorePath = toStorePath(event.newPath, prefix)
          renamePath(oldStorePath, newStorePath)
          break
        }
        case "COPY":
          navigator.clipboard.writeText(event.name)
          break
        case "COPY_PATH":
          navigator.clipboard.writeText(toStorePath(event.path, prefix))
          break
        case "EXTERNAL_CLIPBOARD_PASTE":
          void handleExternalClipboardPaste(event.targetPath)
          break
      }
    },
    [openFile, renamePath, deletePath, createFileAtPath, createFolder, prefix, handleExternalClipboardPaste],
  )

  // OS files dropped onto the explorer (real File objects, forwarded by the Flutter iframe over
  // postMessage — see web_drag_blocker.dart's OS_FILES_DROP). Uploads each one through the same
  // chunked/resumable upload-session pipeline and shared live-job store the dashboard's upload page
  // drives (lib/upload-session.ts + lib/upload-job-store.ts), so progress shows up in the same place
  // (e.g. the Job History view) and a large/interrupted upload can be resumed from there too.
  const handleOsFilesDrop = useCallback(async (files: File[], targetPath: string) => {
    const key = droppedFilesKey(files)
    const now = Date.now()
    const previous = recentOsDropsRef.current.get(key)
    if (previous != null && now - previous < 1000) return
    recentOsDropsRef.current.set(key, now)

    const parentStorePath = toStorePath(targetPath, prefix)
    // Anonymous editor files live in the compiler session, not the project upload API.
    if (!isAuthenticated) {
      await importFinderFiles(files, parentStorePath || null, {
        createFileAtPath,
        updateContent,
        openFile,
      })
      return
    }

    const parentId = parentStorePath ? getFolderId(parentStorePath) : undefined
    let failed = 0

    for (const file of files) {
      try {
        // Always a fresh session — unlike the dashboard's upload page, this drop has no paused-session
        // fingerprint matching to resume an interrupted upload of the same file; a dropped file always
        // starts a brand-new session (a genuinely interrupted one is still resumable from Job History).
        const session = await initUploadSession(file, { project_id: projectId, parent_id: parentId })
        await savePendingUploadSessionBatch([session])

        await uploadFileWithSession(
          file,
          session,
          (pct) => patchLiveUploadJob(session.uploadSessionId, { progress: pct }),
          async () => false, // no pause control surfaced for this drop — runs straight through
          uploadXhrRef,
          (uploadSessionId) => {
            upsertLiveUploadJob({ uploadSessionId, fileName: file.name, fileSize: file.size, progress: 0, paused: false })
          },
        )
        removeLiveUploadJob(session.uploadSessionId)
        notifyUploadJobsChanged()
      } catch (err) {
        failed += 1
        console.warn("[FileExplorerFrame] upload failed for", file.name, err)
      }
    }

    if (failed > 0) {
      toast.error(failed === files.length ? "Could not add the dropped files" : "Some dropped files could not be added")
    }

    // The upload-session API registers each file server-side directly — it never goes through
    // createFileAtPath/POST /api/files — so the tree needs an explicit refresh to show the result.
    refreshFileTree()
  }, [prefix, projectId, getFolderId, refreshFileTree, isAuthenticated, createFileAtPath, updateContent, openFile])

  const respondToDelete = useCallback((confirmed: boolean) => {
    setPendingDelete((current) => {
      if (!current) return current
      const win = iframeRef.current?.contentWindow
      win?.postMessage(
        JSON.stringify({ type: "CONFIRM_DELETE_RESULT", requestId: current.requestId, confirmed }),
        "*",
      )
      return null
    })
  }, [])

  // Keep the message handlers behind refs so the listener below can mount exactly ONCE. If it
  // re-bound whenever these callbacks' identities changed (they depend on prefix/projectId/the
  // file tree), a cold load could rip out the listener at the exact moment the iframe posts its
  // one-shot FRAME_READY — React would miss it, never call postFilesToIframe(), and the tree
  // would never reach the iframe ("files not loading" until a remount re-sends them).
  const handleEventRef = useRef(handleEvent)
  const handleOsFilesDropRef = useRef(handleOsFilesDrop)
  useEffect(() => { handleEventRef.current = handleEvent }, [handleEvent])
  useEffect(() => { handleOsFilesDropRef.current = handleOsFilesDrop }, [handleOsFilesDrop])

  useEffect(() => {
    // The explorer is an iframe. A Finder drag is rejected unless the parent page
    // accepts it (preventDefault + dropEffect "copy") while the pointer is over
    // that iframe. The iframe then posts OS_FILES_DROP with the hovered folder.
    // If that message never arrives, import the same files at the project root.
    const accept = (event: DragEvent) => {
      if (!isExternalFileDrag(event.dataTransfer)) return
      const node = event.target
      if (!(node instanceof Node) || !explorerPanelRef.current?.contains(node)) return
      event.preventDefault()
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy"
    }
    const onDrop = (event: DragEvent) => {
      if (!event.dataTransfer || !isExternalFileDrag(event.dataTransfer)) return
      const node = event.target
      if (!(node instanceof Node) || !explorerPanelRef.current?.contains(node)) return
      const files = filesFromDataTransfer(event.dataTransfer)
      if (files.length === 0) return
      event.preventDefault()
      window.setTimeout(() => {
        void handleOsFilesDropRef.current(files, "")
      }, 200)
    }
    document.addEventListener("dragenter", accept, true)
    document.addEventListener("dragover", accept, true)
    document.addEventListener("drop", onDrop, true)
    return () => {
      document.removeEventListener("dragenter", accept, true)
      document.removeEventListener("dragover", accept, true)
      document.removeEventListener("drop", onDrop, true)
    }
  }, [])

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== iframeRef.current?.contentWindow) return

      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data
        if (data && typeof data === "object") {
          if (data.type === "FRAME_READY") {
            setFrameReady(true)
          } else if (data.type === "CONFIRM_DELETE") {
            setPendingDelete({ requestId: data.requestId, node: data.node })
          } else if (data.type === "OS_FILES_DROP") {
            // data.files are real File objects — they survive postMessage's structured clone
            // as-is, not JSON, so this must be intercepted here rather than falling through to
            // handleEvent/ExplorerEvent (which only ever carries JSON-safe fields).
            const files = filesFromDropMessage(data.files)
            const targetPath = typeof data.targetPath === "string" ? data.targetPath : ""
            if (files.length > 0) void handleOsFilesDropRef.current(files, targetPath)
          } else if ("type" in data) {
            handleEventRef.current(data as ExplorerEvent)
          }
        }
      } catch (err) {
        console.warn("Failed to parse message from iframe:", err)
      }
    }
    window.addEventListener("message", handler)
    return () => window.removeEventListener("message", handler)
  }, [])

  return (
    <div className="flex h-full w-full gap-2 bg-[var(--ed-explorer-canvas-bg)] p-2">
      {/* ── LEFT ICON SIDEBAR ── */}
      <div className="flex shrink-0 flex-col items-center gap-1.5 rounded-2xl border border-[var(--ed-explorer-sidebar-border)] bg-[var(--ed-explorer-sidebar-bg)] px-2 py-3 shadow-sm">
        {sidebarIcons.map((item) => {
          const isActive = activeTab === item.id
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (isActive && !panelCollapsed) {
                  setPanelCollapsed(true)
                } else {
                  setPanelCollapsed(false)
                  setActiveTab(item.id as SidebarTab)
                }
              }}
              className="flex items-center justify-center rounded-xl p-0.5 transition-colors"
              title={item.title}
            >
              <Image
                src={item.src}
                width={item.size}
                height={item.size}
                alt={item.title}
                style={{ filter: `var(${item.id === "file" ? "--ed-icon-filter" : "--ed-icon-filter-mono"}, none)` }}
                className={cn(
                  isActive ? "opacity-100" : "opacity-50 hover:opacity-75",
                )}
              />
            </button>
          )
        })}
        <div className="flex-1" />
        {bottomSidebarIcons.map((item) => (
          <button
            key={item.id}
            type="button"
            title={item.title}
            className="flex items-center justify-center rounded-xl p-0.5 transition-opacity opacity-50 hover:bg-[var(--ed-explorer-icon-hover-bg)] hover:opacity-75"
            onClick={
              item.id === "logout"
                ? () => {
                    void logout().then(() => {
                      window.location.href = "/"
                    })
                  }
                : item.id === "help"
                  ? () => {
                      window.open("/help", "_blank", "noopener,noreferrer")
                    }
                  : undefined
            }
          >
            <Image
              src={item.src}
              width={32}
              height={32}
              alt={item.title}
              style={{ filter: "var(--ed-icon-filter-mono, none)" }}
            />
          </button>
        ))}
      </div>

      {/* ── MAIN PANEL — keep explorer iframe mounted so tree/presence survive settings tab ── */}
      <div className={panelCollapsed ? "hidden" : "relative min-h-0 flex-1"}>
        {/* Settings nav */}
        <div
          className={cn(
            "absolute inset-0 flex flex-col gap-2",
            activeTab === "setting" ? "z-10" : "pointer-events-none z-0 hidden",
          )}
          aria-hidden={activeTab !== "setting"}
        >
           {/*this is commented because already showing that same thing*/}
          {/*<h2*/}
          {/*    className="shrink-0 px-1 pt-1 text-[15px] font-semibold leading-none text-foreground dark:text-neutral-100"*/}
          {/*    style={{ color: "var(--ed-user-header, #5B8CFF)" }}*/}
          {/*>*/}
          {/*  Defaults*/}
          {/*</h2>*/}
          <div
            className="min-h-0 flex-1 overflow-hidden rounded-2xl border border-[var(--ed-explorer-panel-border)] bg-[var(--ed-explorer-panel-bg)]"
            style={{ boxShadow: "var(--ed-explorer-panel-shadow)" }}
          >
            <ProjectSettingsNav />
          </div>
        </div>

        {/* File explorer */}
        <div
          ref={explorerPanelRef}
          className={cn(
            "absolute inset-0 flex flex-col overflow-hidden rounded-2xl border border-[var(--ed-explorer-panel-border)] bg-[var(--ed-explorer-panel-bg)]",
            activeTab === "file" ? "z-10" : "pointer-events-none z-0 invisible",
          )}
          style={{ boxShadow: "var(--ed-explorer-panel-shadow)" }}
          aria-hidden={activeTab !== "file"}
        >
          <div className="min-h-0 flex-1">
            <iframe
              ref={iframeRef}
              src="/explorer/index.html"
              className="block h-full w-full border-none"
              title="File Explorer"
            />
          </div>

          {!isAuthenticated && (
            <>
              <DividerHandle
                axis="horizontal"
                onMouseDown={bottomPanel.onMouseDown}
                onDoubleClick={bottomPanel.onDoubleClick}
              />
              <div
                ref={bottomPanel.targetRef}
                className="shrink-0 overflow-y-auto"
                style={{ height: `${bottomPanel.value}px`, minHeight: "120px", maxHeight: "600px" }}
              >
                <div className="mx-3 mb-3 rounded-xl border border-red-300 bg-white px-4 py-3">
                  <div className="mb-1.5 flex items-start gap-2">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    <span className="text-[13px] leading-snug text-red-600">
                      This commit is not being saved
                    </span>
                  </div>
                  <p className="mb-3 text-xs text-gray-600">
                    Save your progress by <span className="text-blue-600">Signup</span> below
                  </p>
                  <Button className="h-9 w-full rounded-full bg-blue-600 text-sm font-medium text-white hover:bg-blue-700">
                    Sign Up
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {pendingDelete && (
        <DeleteConfirmDialog
          name={pendingDelete.node.name}
          kind={pendingDelete.node.type}
          onCancel={() => respondToDelete(false)}
          onConfirm={() => respondToDelete(true)}
        />
      )}
    </div>
  )
}

/* ── Delete confirmation dialog — shown in place of the Flutter explorer's own dialog
   when embedded (it defers to CONFIRM_DELETE/CONFIRM_DELETE_RESULT over postMessage). ── */
function DeleteConfirmDialog({
  name,
  kind,
  onCancel,
  onConfirm,
}: {
  name: string
  kind: string
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
      onMouseDown={onCancel}
    >
      <div
        className="w-[380px] bg-white rounded-2xl shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-red-500" />
            </div>
            <span className="text-[15px] font-semibold text-gray-900">
              Delete {kind === "folder" ? "Folder" : "File"}
            </span>
          </div>
          <button
            onClick={onCancel}
            className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-5">
          <p className="text-[13px] text-gray-600 leading-relaxed">
            Are you sure you want to delete {kind}{" "}
            <span className="font-semibold text-gray-900">&quot;{name}&quot;</span>?
            <br />
            <span className="text-red-500">This action cannot be undone.</span>
          </p>
        </div>

        <div className="flex gap-2 px-5 pb-5">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 text-[13px] font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 text-[13px] font-semibold text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

